import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Plus, Check, X, CalendarDays, Trash2, Undo2, ShieldCheck, AlertCircle, ThumbsUp, ThumbsDown, Sparkles, Loader2, Mail } from 'lucide-react';
import { wallDateTime, wallDate, wallTime } from '../../lib/time';
import { safeConfirm } from '../../lib/confirm';
import { toast } from 'sonner';
import RecycleBinCard from '../../components/RecycleBinCard';

export default function AdminAppointments() {
  const [appts, setAppts] = useState([]);
  const [pending, setPending] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [services, setServices] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    patient_id: '', starts_at: '', duration_min: 45, price: 200, currency: 'PLN', notes: '', therapist_name: 'Dr. Smith', clinician_id: '', service_id: ''
  });

  // "Where's the next free slot?" picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerFilter, setPickerFilter] = useState({ clinician_id: '', service_id: '', duration: 45, horizon_days: 14 });
  const [pickerSlots, setPickerSlots] = useState([]);

  const load = () => Promise.all([
    api.get('/appointments').then(r => setAppts(r.data)),
    api.get('/appointments/pending-approval').then(r => setPending(r.data)).catch(() => setPending([])),
  ]);
  useEffect(() => {
    load();
    api.get('/patients').then(r => setPatients(r.data));
    api.get('/clinicians').then(r => setClinicians(r.data || [])).catch(() => {});
    api.get('/services').then(r => setServices((r.data || []).filter(s => s.active))).catch(() => {});
  }, []);

  const findNextSlots = async () => {
    setPickerLoading(true);
    try {
      const params = {
        duration: pickerFilter.duration,
        horizon_days: pickerFilter.horizon_days,
        limit: 15,
      };
      if (pickerFilter.clinician_id) params.clinician_id = pickerFilter.clinician_id;
      const { data } = await api.get('/clinicians/next-free-slots', { params });
      setPickerSlots(data || []);
    } catch (e) {
      alert('Failed to find slots: ' + (e.response?.data?.detail || e.message));
    } finally { setPickerLoading(false); }
  };

  const applySlot = (slot) => {
    const svc = services.find(s => s.service_id === pickerFilter.service_id);
    setForm({
      ...form,
      starts_at: slot.starts_at.slice(0, 16),  // datetime-local format
      duration_min: slot.duration_min,
      clinician_id: slot.clinician_id,
      therapist_name: slot.clinician_name,
      service_id: pickerFilter.service_id || form.service_id,
      price: svc ? svc.price : form.price,
      currency: svc ? svc.currency : form.currency,
    });
    setPickerOpen(false);
    setShow(true);
  };

  const submit = async () => {
    await api.post('/appointments', { ...form, price: parseFloat(form.price), duration_min: parseInt(form.duration_min) });
    setShow(false); load();
  };

  const updateStatus = async (a, status) => {
    await api.put(`/appointments/${a.appointment_id}`, { status });
    load();
  };

  // Iter 100 — manual no-show recovery trigger.
  const recoverNoShow = async (a) => {
    if (!await safeConfirm(`Send a reschedule email with 3 suggested slots to ${a.patient_name || 'this patient'}?`)) return;
    try {
      const { data } = await api.post(`/no-show/recover/${a.appointment_id}`);
      alert(data.sent ? `Recovery email sent. The patient will see 3 one-click reschedule slots.` : 'Could not send recovery email (no email on file?).');
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const removeAppt = async (a) => {
    if (!await safeConfirm(`Delete this appointment for ${a.patient_name}? It moves to "Recently deleted" below — restorable for 30 days, then erased permanently.`)) return;
    await api.delete(`/appointments/${a.appointment_id}`);
    toast.success('Appointment moved to Recently deleted — 30-day undo available at the bottom of this page.');
    load();
  };

  const refundPrepay = async (a) => {
    const amount = a.prepay_amount ?? (a.price * (a.prepay_percent || 50) / 100);
    if (!await safeConfirm(`Mark the ${amount?.toFixed ? amount.toFixed(2) : amount} ${a.prepay_currency || a.currency} prepayment as refunded?\n\nIssue the actual refund off-platform (BLIK / cash) first, then confirm here.`)) return;
    try {
      const fd = new FormData();
      const ref = window.prompt('Refund reference (optional — e.g. BLIK transaction code):', '');
      if (ref) fd.append('reference', ref);
      const { data } = await api.post(`/appointments/${a.appointment_id}/mark-refunded`, fd);
      alert(`Marked refunded · ${data.amount?.toFixed(2)} ${a.prepay_currency || a.currency}${data.reference ? ' · ref ' + data.reference : ''}`);
      load();
    } catch (e) {
      alert('Could not mark refunded: ' + (e.response?.data?.detail || e.message));
    }
  };

  const approveAppt = async (a) => {
    await api.post(`/appointments/${a.appointment_id}/approve`);
    load();
  };
  const rejectAppt = async (a) => {
    const reason = window.prompt('Reason for rejection (optional, sent to patient):', '');
    await api.post(`/appointments/${a.appointment_id}/reject`, null, { params: reason ? { reason } : {} });
    load();
  };

  const verifyPayment = async (a) => {
    if (!await safeConfirm(`Verify that ${a.patient_name}'s ${a.prepay_method ? a.prepay_method.toUpperCase() : 'prepayment'} transfer was received?\nReference: ${a.prepay_reference || '(none)'}`)) return;
    try {
      await api.post(`/appointments/${a.appointment_id}/verify-payment`);
      load();
    } catch (e) {
      alert('Verify failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CALENDAR</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Appointments</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setPickerOpen(o=>!o); if (!pickerOpen && pickerSlots.length === 0) findNextSlots(); }}
                  className="btn-secondary inline-flex items-center gap-2" data-testid="next-slot-btn">
            <Sparkles className="w-4 h-4"/> {pickerOpen ? 'Close' : "Next free slot"}
          </button>
          <button onClick={() => setShow(!show)} className="btn-primary inline-flex items-center gap-2" data-testid="new-appt-btn">
            <Plus className="w-4 h-4" /> {show ? 'Close' : 'Schedule'}
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div className="admin-card mb-6 border-l-4 border-l-brand" data-testid="next-slot-picker">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-brand"/>
            <h3 className="font-heading font-semibold">Where's the next free slot?</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">Search across all active clinicians (or pick one) for the soonest open slot. Tap a slot to pre-fill the booking form.</p>
          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs text-fg-muted">Clinician</label>
              <select className="input" value={pickerFilter.clinician_id}
                      onChange={e=>setPickerFilter({...pickerFilter, clinician_id: e.target.value})}
                      data-testid="picker-clinician">
                <option value="">Any (across the team)</option>
                {clinicians.map(c => <option key={c.user_id} value={c.user_id}>{c.name}{c.credentials?`, ${c.credentials}`:''}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-fg-muted">Service</label>
              <select className="input" value={pickerFilter.service_id}
                      onChange={e=>{ const sv = services.find(s=>s.service_id===e.target.value); setPickerFilter({...pickerFilter, service_id: e.target.value, duration: sv ? sv.duration_min : pickerFilter.duration}); }}
                      data-testid="picker-service">
                <option value="">Custom duration</option>
                {services.map(s => <option key={s.service_id} value={s.service_id}>{s.name} · {s.duration_min}m</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-fg-muted">Duration (min)</label>
              <input type="number" className="input" value={pickerFilter.duration}
                     onChange={e=>setPickerFilter({...pickerFilter, duration: parseInt(e.target.value) || 45})}
                     data-testid="picker-duration"/>
            </div>
            <div>
              <label className="text-xs text-fg-muted">Search window (days)</label>
              <input type="number" min="1" max="60" className="input" value={pickerFilter.horizon_days}
                     onChange={e=>setPickerFilter({...pickerFilter, horizon_days: Math.max(1, Math.min(60, parseInt(e.target.value) || 14))})}
                     data-testid="picker-horizon"/>
            </div>
          </div>
          <button onClick={findNextSlots} disabled={pickerLoading} className="btn-primary inline-flex items-center gap-2 mb-3" data-testid="picker-search">
            {pickerLoading && <Loader2 className="w-4 h-4 animate-spin"/>} Find slots
          </button>
          {pickerSlots.length === 0 && !pickerLoading && (
            <div className="text-sm text-fg-muted py-3 italic" data-testid="picker-empty">
              No free slots in the next {pickerFilter.horizon_days} days. Try widening the window or selecting a different clinician.
            </div>
          )}
          {pickerSlots.length > 0 && (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="picker-results">
              {pickerSlots.map((slot, i) => {
                const day = wallDate(slot.starts_at);
                const time = wallTime(slot.starts_at);
                return (
                  <button key={i} onClick={()=>applySlot(slot)} type="button"
                          className="text-left surface border border-soft rounded-lg p-3 hover:border-brand hover:bg-brand/5 transition"
                          data-testid={`picker-slot-${i}`}>
                    <div className="text-xs text-fg-muted">{day}</div>
                    <div className="font-heading font-bold text-lg">{time}</div>
                    <div className="text-xs text-fg-secondary truncate mt-0.5">
                      {slot.clinician_name}{slot.credentials ? ` · ${slot.credentials}` : ''}
                    </div>
                    <div className="text-[10px] text-brand mt-1">Tap to book →</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {show && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-4">New appointment</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-fg-muted">Patient</label>
              <select className="input" value={form.patient_id} onChange={e=>setForm({...form, patient_id: e.target.value})} data-testid="appt-patient">
                <option value="">Select…</option>
                {patients.map(p => <option key={p.user_id} value={p.user_id}>{p.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-fg-muted">Starts at</label>
              <input type="datetime-local" className="input" value={form.starts_at} onChange={e=>setForm({...form, starts_at: e.target.value})} data-testid="appt-time" />
            </div>
            <div>
              <label className="text-xs text-fg-muted">Duration (min)</label>
              <input type="number" className="input" value={form.duration_min} onChange={e=>setForm({...form, duration_min: e.target.value})} data-testid="appt-duration" />
            </div>
            <div>
              <label className="text-xs text-fg-muted">Price</label>
              <input type="number" className="input" value={form.price} onChange={e=>setForm({...form, price: e.target.value})} data-testid="appt-price" />
            </div>
            <div>
              <label className="text-xs text-fg-muted">Currency</label>
              <select className="input" value={form.currency} onChange={e=>setForm({...form, currency: e.target.value})} data-testid="appt-currency">
                <option>PLN</option><option>EUR</option><option>USD</option><option>CAD</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-fg-muted">Clinician</label>
              <select className="input" value={form.clinician_id}
                      onChange={e=>{ const c=clinicians.find(x=>x.user_id===e.target.value); setForm({...form, clinician_id: e.target.value, therapist_name: c ? c.name : form.therapist_name}); }}
                      data-testid="appt-clinician">
                <option value="">No preference</option>
                {clinicians.map(c => <option key={c.user_id} value={c.user_id}>{c.name}{c.credentials?`, ${c.credentials}`:''}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-fg-muted">Notes</label>
              <input className="input" value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} data-testid="appt-notes" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={submit} className="btn-primary" disabled={!form.patient_id || !form.starts_at} data-testid="appt-save">Save</button>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="admin-card mb-6 border-l-4 border-l-[#F2B33D]" data-testid="pending-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-[#F2B33D]"/>
            <h3 className="font-heading font-semibold">Pending approvals — {pending.length}</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">These patients self-booked with a clinician set to require approval. Approve or reject before the slot is held.</p>
          <table className="text-sm w-full">
            <thead><tr><th>Patient</th><th>Clinician</th><th>Time</th><th></th></tr></thead>
            <tbody>
              {pending.map(a => (
                <tr key={a.appointment_id} data-testid={`pending-row-${a.appointment_id}`}>
                  <td>
                    <div className="flex items-center gap-1.5">
                      {a.patient_name}
                      {a.new_condition && <NewConditionPill testid={`pending-new-cond-${a.appointment_id}`}/>}
                      {a.is_first_visit && <FirstVisitPill testid={`pending-first-${a.appointment_id}`}/>}
                    </div>
                    {a.reason_for_visit && (
                      <div className="text-[11px] text-fg-muted mt-0.5 italic max-w-md truncate" title={a.reason_for_visit}>
                        "{a.reason_for_visit}"
                      </div>
                    )}
                  </td>
                  <td className="text-fg-muted">{a.therapist_name}</td>
                  <td>{wallDateTime(a.starts_at)}
                    {a.location_name && (
                      <div className="text-[10px] text-fg-muted uppercase tracking-wider mt-0.5" data-testid={`pending-loc-${a.appointment_id}`}>📍 {a.location_name}</div>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={()=>approveAppt(a)} className="btn-secondary text-xs inline-flex items-center gap-1 mr-2 !text-[#3A664D]" data-testid={`approve-${a.appointment_id}`}>
                      <ThumbsUp className="w-3.5 h-3.5"/> Approve
                    </button>
                    <button onClick={()=>rejectAppt(a)} className="btn-secondary text-xs inline-flex items-center gap-1 !text-[#B83A3A]" data-testid={`reject-${a.appointment_id}`}>
                      <ThumbsDown className="w-3.5 h-3.5"/> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-card">
        <table>
          <thead><tr><th>Patient</th><th>Time</th><th>Duration</th><th>Status</th><th>Price</th><th>Actions</th></tr></thead>
          <tbody>
            {appts.map(a => (
              <tr key={a.appointment_id} data-testid={`appt-row-${a.appointment_id}`}
                  className={a.new_condition ? 'bg-[#FFF4D6]/40' : ''}>
                <td>
                  <div className="flex items-center gap-1.5">
                    {a.patient_name}
                    {a.new_condition && <NewConditionPill testid={`new-cond-${a.appointment_id}`}/>}
                    {a.is_first_visit && <FirstVisitPill testid={`first-${a.appointment_id}`}/>}
                  </div>
                  {a.reason_for_visit && (
                    <div className="text-[11px] text-fg-muted mt-0.5 italic max-w-md truncate" title={a.reason_for_visit}>
                      "{a.reason_for_visit}"
                    </div>
                  )}
                </td>
                <td>{wallDateTime(a.starts_at)}</td>
                <td>{a.duration_min} min</td>
                <td><StatusBadge s={a.status} /></td>
                <td>{a.price?.toFixed(2)} {a.currency}</td>
                <td className="flex gap-1.5 flex-wrap">
                  <button onClick={()=>updateStatus(a, 'completed')} className="text-[#3A664D] p-1" title="Complete" data-testid={`complete-${a.appointment_id}`}><Check className="w-4 h-4"/></button>
                  <button onClick={()=>updateStatus(a, 'cancelled')} className="text-[#B83A3A] p-1" title="Cancel" data-testid={`cancel-${a.appointment_id}`}><X className="w-4 h-4"/></button>
                  {/* Iter 100 — Recover no-show button only shown when status === 'no_show' */}
                  {a.status === 'no_show' && (
                    <button onClick={()=>recoverNoShow(a)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#003D80] border border-[#003D80]/30 hover:bg-[#003D80]/10 rounded px-2 py-0.5"
                            title="Email patient 3 one-click reschedule slots"
                            data-testid={`recover-noshow-${a.appointment_id}`}>
                      <Mail className="w-3.5 h-3.5"/> Recover
                    </button>
                  )}
                  {a.prepay_status === 'pending_verification' && (
                    <button onClick={()=>verifyPayment(a)} className="text-[#0075EB] p-1 inline-flex items-center gap-1 text-xs font-medium border border-[#0075EB]/30 rounded px-2"
                            title="Verify BLIK payment received" data-testid={`verify-${a.appointment_id}`}>
                      <ShieldCheck className="w-3.5 h-3.5"/> Verify {a.prepay_method}
                    </button>
                  )}
                  {a.prepay_status === 'awaiting_payment' && (
                    <span className="badge-warn text-[10px]" title={`Awaiting ${a.prepay_method} transfer`}>awaiting {a.prepay_method}</span>
                  )}
                  {a.prepay_status === 'paid' && !a.refund_reference && (
                    <button onClick={()=>refundPrepay(a)} className="text-[#FFB000] p-1" title="Mark refunded (off-platform)" data-testid={`refund-${a.appointment_id}`}>
                      <Undo2 className="w-4 h-4"/>
                    </button>
                  )}
                  {(a.prepay_status === 'refunded' || a.refund_reference) && (
                    <span className="badge-success text-[10px]" title={`Refund ref: ${a.refund_reference || '—'}`}>refunded</span>
                  )}
                  {a.refund_required && a.prepay_status !== 'refunded' && (
                    <button onClick={()=>refundPrepay(a)} className="text-[#B83A3A] p-1 inline-flex items-center gap-1 text-xs font-medium border border-[#B83A3A]/30 rounded px-2"
                            title="Refund required after cancellation" data-testid={`refund-required-${a.appointment_id}`}>
                      <Undo2 className="w-3.5 h-3.5"/> Refund required
                    </button>
                  )}
                  <button onClick={()=>removeAppt(a)} className="text-fg-muted hover:text-[#B83A3A] p-1" title="Delete" data-testid={`delete-appt-${a.appointment_id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {!appts.length && <tr><td colSpan="6" className="text-center py-8 text-fg-muted">
              <CalendarDays className="w-8 h-8 mx-auto mb-2 text-fg-muted" />
              No appointments yet.
            </td></tr>}
          </tbody>
        </table>
      </div>

      <RecycleBinCard kind="appointment" noun="Appointment" onRestored={load}/>
    </div>
  );
}

function StatusBadge({ s }) {
  const cls = s === 'completed' ? 'badge-success' : s === 'scheduled' ? 'badge-neutral'
              : s === 'cancelled' ? 'badge-error' : 'badge-warn';
  return <span className={cls}>{s}</span>;
}

function NewConditionPill({ testid }) {
  return (
    <span title="Patient reports a new condition since their last visit — review the reason note below"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#FFB000] text-[#5A3A00]"
          data-testid={testid}>
      <AlertCircle className="w-3 h-3"/> NEW CONDITION
    </span>
  );
}

function FirstVisitPill({ testid }) {
  return (
    <span title="First visit — review the patient's intake form before the session"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand/15 text-brand"
          data-testid={testid}>
      FIRST VISIT
    </span>
  );
}
